import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { listPatients, createPatient, listEnrolmentOrgs } from '../../api/patients.js'
import { listEncounters, createEncounter } from '../../api/encounters.js'
import { listBookings } from '../../api/bookings.js'
import { listEmergencies } from '../../api/emergencies.js'
import {
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../../api/notifications.js'
import { useDebounce } from '../../hooks/useDebounce.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import EmergencyInbox from './EmergencyInbox.jsx'
import TriageBadge from '../../components/TriageBadge.jsx'
import TriageSummaryCard from '../../components/TriageSummaryCard.jsx'
import {
  formatDateTime,
  statusColor,
  isLikelyDuplicatePatientError,
  getDuplicatePatientCandidates,
} from '../../utils/format.js'
import DuplicatePatientModal from '../../components/DuplicatePatientModal.jsx'
import { ENCOUNTER_STATUS } from '../../constants/index.js'
import { DoctorScreeningList } from './DoctorScreening.jsx'

// Tabbed layout — each tab is an independent workspace so the doctor
// doesn't scroll a long stacked page. The active tab is persisted in
// localStorage so a refresh lands them back where they were.
const TABS = [
  // Emergency inbox — RED triage cases routed to this doctor by clinical
  // responsibility. First so it's impossible to miss; red count badge.
  { id: 'emergency',     label: 'Emergency' },
  { id: 'register',      label: 'Register Patient' },
  { id: 'encounter',     label: 'New Encounter' },
  // Upcoming = bookings on this doctor's available slots that are still
  // in scheduled state. Distinct from drafts (in-progress encounters)
  // and submitted (already finalised). The doctor opens the
  // appointment from here at slot time → encounter editor.
  { id: 'upcoming',      label: 'Upcoming' },
  { id: 'drafts',        label: 'My Draft Encounters' },
  { id: 'submitted',     label: 'Submitted Encounters' },
  // Routine medical screening — campaigns the doctor can screen patients
  // into. The campaign list lives here; the per-campaign working surface
  // is its own route (/doctor/screening/campaigns/:id).
  { id: 'screening',     label: 'Screening' },
  { id: 'notifications', label: 'Notifications' },
]
const TAB_STORAGE_KEY = 'wantok.doctor.activeTab'

export default function DoctorDashboard() {
  const [q, setQ] = useState('')
  const debouncedQ = useDebounce(q, 250)
  const nav = useNavigate()
  const qc = useQueryClient()

  // Active tab — persisted across refreshes so a doctor mid-workflow
  // doesn't lose context if they reload. Default lands on Drafts since
  // that's the "resume where I left off" view.
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const v = localStorage.getItem(TAB_STORAGE_KEY)
      return TABS.some((t) => t.id === v) ? v : 'drafts'
    } catch { return 'drafts' }
  })
  function changeTab(id) {
    setActiveTab(id)
    try { localStorage.setItem(TAB_STORAGE_KEY, id) } catch {}
  }

  // Chief-complaint capture-at-intake state. When the doctor clicks
  // "New encounter" we open a tiny dialog asking for a one-line chief
  // complaint before the encounter row is created. Filling it in is
  // optional — doctors can still skip and edit later from the workspace.
  const [ccDialog, setCcDialog] = useState(null) // { patient } | null
  const [ccText, setCcText] = useState('')

  const patients = useQuery({
    queryKey: ['patients', debouncedQ],
    queryFn: () => listPatients({ q: debouncedQ }),
  })

  const drafts = useQuery({
    queryKey: ['encounters', { status: ENCOUNTER_STATUS.DRAFT }],
    queryFn: () => listEncounters({ status: ENCOUNTER_STATUS.DRAFT }),
  })

  const submitted = useQuery({
    queryKey: ['encounters', { status: ENCOUNTER_STATUS.SUBMITTED }],
    queryFn: () => listEncounters({
      status: ENCOUNTER_STATUS.SUBMITTED,
      ordering: '-updated_at',
    }),
    // Only fetch when the doctor opens this tab — submitted encounters
    // can be a long list and we don't want to pay for the query when
    // they're working on a draft.
    enabled: activeTab === 'submitted',
  })

  // Upcoming bookings on this doctor's available slots. The backend's
  // BookingViewSet auto-scopes to the doctor when the requesting user
  // is a doctor, so a plain list call returns only their own.
  const upcoming = useQuery({
    queryKey: ['doctor-upcoming-bookings'],
    queryFn: () => listBookings({ status: 'scheduled', ordering: '-slot__slot_at' }),
    // Refetch periodically so a fresh booking by a patient shows up
    // without the doctor refreshing the page.
    refetchInterval: 30_000,
    enabled: activeTab === 'upcoming',
  })

  // Emergency inbox — always polling (even on other tabs) so the red tab
  // badge reflects new RED cases the moment they're routed here.
  const emergencies = useQuery({
    queryKey: ['doctor-emergencies'],
    queryFn: listEmergencies,
    refetchInterval: 20_000,
  })

  // Reusable list-search state. Keeps each tab's filter independent so
  // switching between Drafts and Submitted preserves what you typed.
  const [draftsSearch, setDraftsSearch] = useState('')
  const [submittedSearch, setSubmittedSearch] = useState('')

  const createEnc = useMutation({
    mutationFn: ({ patientId, chief_complaint }) =>
      createEncounter(patientId, chief_complaint ? { chief_complaint } : {}),
    onSuccess: (enc) => {
      qc.invalidateQueries({ queryKey: ['encounters'] })
      setCcDialog(null)
      setCcText('')
      nav(`/doctor/encounters/${enc.id}`)
    },
  })

  function openCcDialog(patient) {
    setCcText('')
    setCcDialog({ patient })
  }
  function startEncounter() {
    if (!ccDialog) return
    createEnc.mutate({
      patientId: ccDialog.patient.id,
      chief_complaint: ccText.trim(),
    })
  }

  // Notifications stream — same data the patient home uses, scoped per
  // user. Auto-refreshes every 10 s so lab/imaging-ready alerts surface
  // close to real-time without a manual reload.
  const notifQ = useQuery({
    queryKey: ['doctor-notif'],
    queryFn: () => listNotifications(),
    refetchInterval: 10_000,
  })
  const markRead = useMutation({
    mutationFn: (id) => markNotificationRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['doctor-notif'] }),
  })
  const markAll = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['doctor-notif'] }),
  })
  const unreadCount = (notifQ.data || []).filter((n) => !n.read_at).length

  // Counts surfaced on the tab strip so doctors see what's pending
  // without clicking through.
  const draftCount = (drafts.data || []).length
  const emergencyCount = (emergencies.data || []).length

  return (
    <div className="space-y-4">
      {/* ---- Tab strip ---- */}
      <div className="border-b-2 border-slate-200 flex flex-wrap gap-2">
        {TABS.map((t) => {
          const active = activeTab === t.id
          // Show counts as badges on tabs that have queryable counts.
          const badge =
            t.id === 'notifications' ? unreadCount :
            t.id === 'emergency'     ? emergencyCount :
            t.id === 'drafts'        ? draftCount  : null
          return (
            <button
              key={t.id}
              onClick={() => changeTab(t.id)}
              className={
                `px-6 py-3 text-base tracking-wide border-b-[3px] -mb-[2px] transition-colors ` +
                (active
                  ? 'border-wantok-700 text-wantok-800 font-bold bg-wantok-50/60'
                  : 'border-transparent text-slate-600 font-semibold hover:text-wantok-800 hover:bg-slate-50')
              }
            >
              {t.label}
              {badge != null && badge > 0 && (
                <span className={
                  `ml-2 inline-flex items-center justify-center text-[11px] font-bold rounded-full ` +
                  `min-w-[20px] h-[20px] px-1.5 ` +
                  (t.id === 'notifications' || t.id === 'emergency'
                    ? 'bg-coral-500 text-white'
                    : 'bg-wantok-100 text-wantok-800')
                }>{badge}</span>
              )}
            </button>
          )
        })}
      </div>

      {/* ---- Tab panels — only the active one renders ---- */}

      {activeTab === 'register' && (
        <RegisterPatientCard
          onRegistered={(patient, { startEncounter }) => {
            qc.invalidateQueries({ queryKey: ['patients'] })
            if (startEncounter) {
              changeTab('encounter')      // jump to encounter tab
              openCcDialog(patient)        // and open the chief-complaint dialog
            } else {
              changeTab('drafts')          // otherwise show them their drafts
            }
          }}
        />
      )}

      {activeTab === 'encounter' && (
        <Card>
          <CardHeader
            title="Start a new encounter"
            subtitle="Search an existing patient and open their chart, or start a fresh consultation."
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, MRN, phone…"
            className="w-full border rounded px-3 py-2 text-sm"
            inputMode="search"
            autoComplete="off"
            autoFocus
          />
          {patients.error && <ErrorBanner error={patients.error} onRetry={() => patients.refetch()} />}
          <ul className="mt-3 divide-y divide-slate-100">
            {patients.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
            {!patients.isLoading && (patients.data || []).length === 0 && (
              <li className="py-3 text-sm text-slate-400">
                No patients match. <button onClick={() => changeTab('register')} className="text-wantok-700 underline">Register a new patient</button>?
              </li>
            )}
            {(patients.data || []).map((p) => (
              <li key={p.id} className="py-2 flex items-center justify-between gap-2">
                <Link to={`/doctor/patients/${p.id}`} className="min-w-0 flex-1 hover:underline">
                  <div className="text-sm font-medium truncate">{p.full_name || p.name || p.mrn}</div>
                  <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                    {p.mrn && (
                      <span>
                        <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1">MRN</span>
                        <span className="font-mono">{p.mrn}</span>
                      </span>
                    )}
                    {p.sex && <span className="capitalize">{p.sex}</span>}
                    {p.province && <span>{p.province}</span>}
                  </div>
                </Link>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to={`/doctor/patients/${p.id}`}
                    className="text-xs text-wantok-700 underline"
                  >
                    Chart
                  </Link>
                  <button
                    onClick={() => openCcDialog(p)}
                    disabled={createEnc.isPending}
                    className="text-xs bg-wantok-700 hover:bg-wantok-800 text-white px-3 py-1.5 rounded disabled:opacity-60"
                  >
                    {createEnc.isPending ? 'Starting…' : 'New encounter'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {activeTab === 'emergency' && (
        <EmergencyInbox query={emergencies} />
      )}

      {activeTab === 'upcoming' && (
        <Card>
          <CardHeader
            title="Upcoming consultations"
            subtitle="Bookings on your available slots, ordered by appointment time. Open each one when it's time to see the patient — that flips it to a draft encounter."
            right={
              <a
                href="/doctor/availability"
                className="text-xs font-semibold border border-wantok-700 text-wantok-700 hover:bg-wantok-50 rounded px-3 py-1.5 no-underline"
                title="Edit which 15-minute windows you're available for booking."
              >
                Manage availability →
              </a>
            }
          />
          {upcoming.error && <ErrorBanner error={upcoming.error} onRetry={() => upcoming.refetch()} />}
          <ul className="divide-y divide-slate-100">
            {upcoming.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
            {!upcoming.isLoading && (upcoming.data || []).length === 0 && (
              <li className="py-3 text-sm text-slate-400">
                No upcoming bookings. Make sure you've opened some availability slots —
                use the <a href="/doctor/availability" className="text-wantok-700 underline">Manage availability</a> page.
              </li>
            )}
            {(upcoming.data || []).map((b) => {
              const when = new Date(b.slot_at)
              const today = new Date()
              const isToday = when.toDateString() === today.toDateString()
              return (
                <li key={b.id} className="py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800">
                        {when.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })}{' '}
                        <span className={isToday ? 'text-wantok-700' : 'text-slate-700'}>
                          {when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {isToday && <span className="ml-2 text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">today</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <b>{b.patient_name || 'Unknown patient'}</b>
                        <TriageBadge severity={b.triage_severity} />
                        {b.reason && <span>· {b.reason}</span>}
                      </div>
                    </div>
                    {b.encounter_id ? (
                      <a
                        href={`/doctor/encounters/${b.encounter_id}`}
                        className="shrink-0 text-xs font-semibold bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-1.5 no-underline"
                      >
                        Open consultation
                      </a>
                    ) : (
                      <span className="shrink-0 text-[11px] text-slate-400 italic">no encounter</span>
                    )}
                  </div>
                  {b.triage_summary && (
                    <TriageSummaryCard
                      severity={b.triage_severity}
                      summary={b.triage_summary}
                      className="mt-2"
                    />
                  )}
                </li>
              )
            })}
          </ul>
        </Card>
      )}

      {activeTab === 'drafts' && (
        <Card>
          <CardHeader title="My draft encounters" subtitle="Resume where you left off — these are still editable." />
          <ListSearch
            value={draftsSearch}
            onChange={setDraftsSearch}
            placeholder="Search drafts by patient name, MRN, DOB, or chief complaint…"
          />
          {drafts.error && <ErrorBanner error={drafts.error} onRetry={() => drafts.refetch()} />}
          {(() => {
            const all = drafts.data || []
            const filtered = all.filter((e) => matchesEncounter(e, draftsSearch))
            return (
              <ul className="divide-y divide-slate-100">
                {drafts.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
                {!drafts.isLoading && all.length === 0 && (
                  <li className="py-3 text-sm text-slate-400">
                    No drafts. <button onClick={() => changeTab('encounter')} className="text-wantok-700 underline">Start a new encounter</button>?
                  </li>
                )}
                {!drafts.isLoading && all.length > 0 && filtered.length === 0 && (
                  <li className="py-3 text-sm text-slate-400 italic">
                    No drafts match "{draftsSearch}".
                  </li>
                )}
                {filtered.map((enc) => (
                  <li key={enc.id} className="py-2 flex items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium">{enc.patient_name || enc.patient?.full_name || 'Patient'}</div>
                      <div className="text-xs text-slate-500">
                        Updated {formatDateTime(enc.updated_at)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] uppercase border rounded-full px-2 py-0.5 ${statusColor(enc.status)}`}>{enc.status}</span>
                      <Link to={`/doctor/encounters/${enc.id}`} className="text-xs text-wantok-700 underline">Open</Link>
                    </div>
                  </li>
                ))}
              </ul>
            )
          })()}
        </Card>
      )}

      {activeTab === 'submitted' && (
        <Card>
          <CardHeader
            title="Submitted encounters"
            subtitle="Locked, finalised consultations. View, print/export to PDF, or add an addendum."
          />
          <ListSearch
            value={submittedSearch}
            onChange={setSubmittedSearch}
            placeholder="Search submitted encounters by patient name, MRN, DOB, or chief complaint…"
          />
          {submitted.error && <ErrorBanner error={submitted.error} onRetry={() => submitted.refetch()} />}
          {(() => {
            const all = submitted.data || []
            const filtered = all.filter((e) => matchesEncounter(e, submittedSearch))
            return (
              <ul className="divide-y divide-slate-100">
                {submitted.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
                {!submitted.isLoading && all.length === 0 && (
                  <li className="py-3 text-sm text-slate-400">
                    No submitted encounters yet. Once you submit a draft it'll appear here.
                  </li>
                )}
                {!submitted.isLoading && all.length > 0 && filtered.length === 0 && (
                  <li className="py-3 text-sm text-slate-400 italic">
                    No submitted encounters match "{submittedSearch}".
                  </li>
                )}
                {filtered.map((enc) => (
                  <li key={enc.id} className="py-2 flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{enc.patient_name || enc.patient?.full_name || 'Patient'}</div>
                      <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
                        <span>Updated {formatDateTime(enc.updated_at)}</span>
                        {enc.chief_complaint && <span className="truncate max-w-[40ch]">· {enc.chief_complaint}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] uppercase border rounded-full px-2 py-0.5 ${statusColor(enc.status)}`}>{enc.status}</span>
                      <Link to={`/doctor/encounters/${enc.id}`} className="text-xs text-wantok-700 underline">Open</Link>
                      <Link to={`/doctor/encounters/${enc.id}/print`} className="text-xs text-wantok-700 underline">Print / PDF</Link>
                    </div>
                  </li>
                ))}
              </ul>
            )
          })()}
        </Card>
      )}

      {activeTab === 'screening' && <DoctorScreeningList />}

      {activeTab === 'notifications' && (
        <Card>
          <CardHeader
            title="Notifications"
            subtitle={unreadCount ? `${unreadCount} unread` : 'All caught up.'}
            right={
              <button
                onClick={() => markAll.mutate()}
                disabled={!unreadCount}
                className="text-xs text-wantok-700 underline disabled:text-slate-400 disabled:no-underline"
              >
                Mark all read
              </button>
            }
          />
          {notifQ.error && <ErrorBanner error={notifQ.error} onRetry={() => notifQ.refetch()} />}
          <ul className="divide-y divide-slate-100">
            {notifQ.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
            {!notifQ.isLoading && (notifQ.data || []).length === 0 && (
              <li className="py-3 text-sm text-slate-400">No notifications.</li>
            )}
            {(notifQ.data || []).slice(0, 30).map((n) => (
              <li key={n.id} className="py-2.5 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  {/* Title — always slate-900 for legibility. Bold for
                      unread, semibold for read. Metadata sits below in
                      slate-400 so the title clearly leads the eye. */}
                  <div className={
                    `text-[15px] text-slate-900 leading-snug ` +
                    (n.read_at ? 'font-semibold' : 'font-bold')
                  }>
                    {n.title || n.kind}
                  </div>
                  <div className="text-xs text-slate-400 flex flex-wrap gap-2 items-center mt-1">
                    <span className="uppercase border rounded-full px-2 py-0.5 text-[10px] bg-slate-50 border-slate-200 text-slate-600 font-semibold">{n.kind}</span>
                    <span>{formatDateTime(n.created_at)}</span>
                    {n.body && <span className="truncate max-w-[60ch] text-slate-500">· {n.body}</span>}
                  </div>
                </div>
                {!n.read_at && (
                  <button
                    className="text-xs text-wantok-700 underline font-semibold shrink-0"
                    onClick={() => markRead.mutate(n.id)}
                  >
                    Mark read
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Chief-complaint capture-at-intake dialog. Sits at root so it
          overlays whichever tab is active. */}
      {ccDialog && (
        <ChiefComplaintDialog
          patientName={ccDialog.patient.full_name || ccDialog.patient.name || ccDialog.patient.mrn}
          value={ccText}
          onChange={setCcText}
          onStart={startEncounter}
          onCancel={() => { setCcDialog(null); setCcText('') }}
          starting={createEnc.isPending}
          error={createEnc.error}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Chief-complaint dialog — captured at intake, optional, editable later
// ---------------------------------------------------------------------------

function ChiefComplaintDialog({
  patientName, value, onChange, onStart, onCancel, starting, error,
}) {
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl shadow-xl border border-slate-200 max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[11px] uppercase tracking-wide text-slate-400">
          New encounter
        </div>
        <div className="text-base font-semibold text-slate-800 truncate">
          {patientName}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          What's bringing the patient in today? One short line, in their own
          words. You can edit or fill this in later inside the encounter.
        </div>

        <input
          autoFocus
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onStart()
            if (e.key === 'Escape') onCancel()
          }}
          placeholder="e.g. chest pain for 2 days"
          maxLength={250}
          className="mt-3 w-full border rounded px-3 py-2 text-sm"
        />

        {error && <div className="mt-3"><ErrorBanner error={error} /></div>}

        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={starting}
            className="text-sm px-3 py-2 rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={onStart}
            disabled={starting}
            className="text-sm px-3 py-2 rounded border border-wantok-700 text-wantok-700 bg-white hover:bg-wantok-50 disabled:opacity-60"
            title="Start the encounter without filling the chief complaint. You can add it inside the workspace."
          >
            Skip
          </button>
          <button
            onClick={onStart}
            disabled={starting}
            className="text-sm px-3 py-2 rounded bg-wantok-700 hover:bg-wantok-800 text-white disabled:opacity-60"
          >
            {starting ? 'Starting…' : 'Start encounter'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Register-new-patient card
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  sex: 'male',
  date_of_birth: '',
  phone_number: '',
  email_in: '',
  place_of_residence: '',
  patient_category: 'general',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  // Optional org binding — the doctor picks the patient's employer from a
  // dropdown of active subscribing orgs; we send its id to the backend,
  // which creates the OrganizationStaffLink so the patient's care is
  // credited to that org. Blank → no org link → standalone patient.
  organization_id: '',
  employee_id_in: '',
  department_in: '',
  // Consent capture (task #23) — mirrors the two required kinds enforced
  // by the public /register flow. Doctor must tick these with the patient
  // BEFORE submit — HIPAA-material. `data_sharing_with_employer` is
  // optional and only relevant when the patient is being linked to an org.
  consent_tos: false,
  consent_telehealth: false,
  consent_data_sharing: false,
}

function RegisterPatientCard({ onRegistered }) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const navigate = useNavigate()

  // Task #24 — duplicate-patient modal state. When the backend returns 409
  // with a candidates payload, we stash the candidates + the pending payload
  // + the pending startEncounter flag so the doctor's choice on the modal
  // can either navigate to the existing patient's chart OR retry the same
  // submit with force_create=true to override the guard.
  const [dupCandidates, setDupCandidates] = useState(null)
  const [pendingSubmit, setPendingSubmit] = useState(null)

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const mut = useMutation({
    mutationFn: (payload) => createPatient(payload),
  })

  // The "Link to organisation" panel only renders when the doctor
  // picks a non-General category. Switching back to General clears
  // any org/employee/dept they selected so submitting won't carry
  // stale org context.
  const showOrgPanel = form.patient_category !== 'general'
  const setCategory = (e) => {
    const next = e.target.value
    setForm((f) => ({
      ...f,
      patient_category: next,
      ...(next === 'general'
        ? { organization_id: '', employee_id_in: '', department_in: '' }
        : {}),
    }))
  }

  // Active subscribing orgs for the employer dropdown. Fetched lazily —
  // only once the doctor opens the card and selects a non-General category.
  const orgsQuery = useQuery({
    queryKey: ['enrolment-orgs'],
    queryFn: listEnrolmentOrgs,
    enabled: open && showOrgPanel,
    staleTime: 5 * 60 * 1000,
  })

  // Inline validation mirroring patients.validators.require_at_least_one_contact
  // — block submit if both email and phone are blank so the patient has a
  // recovery channel.
  const hasEmail = Boolean((form.email_in || '').trim())
  const hasPhone = Boolean((form.phone_number || '').trim())
  const contactOk = hasEmail || hasPhone
  const namesOk = form.first_name.trim() || form.last_name.trim()
  // Task #23 — Terms of Service + Telehealth Treatment consent MUST be
  // ticked before a walk-in can be registered. Data-sharing is optional.
  const consentsOk = form.consent_tos && form.consent_telehealth
  const formValid = namesOk && contactOk && consentsOk

  async function submit(startEncounter) {
    if (!formValid) return
    const payload = { ...form }
    // Drop blanks so the serializer defaults apply (e.g. DOB nullable).
    if (!payload.date_of_birth) delete payload.date_of_birth
    if (!payload.email_in) delete payload.email_in
    // Org link — only send when the doctor picked an employer from the
    // dropdown. Backend treats blank as "no link" (standalone patient =
    // General-category default). Strip employee/department when no org,
    // since they have no org to anchor to.
    const orgId = parseInt(payload.organization_id, 10)
    if (!orgId) {
      delete payload.organization_id
      delete payload.employee_id_in
      delete payload.department_in
    } else {
      payload.organization_id = orgId
      if (!payload.employee_id_in) delete payload.employee_id_in
      if (!payload.department_in) delete payload.department_in
    }
    // Convert the 3 checkbox booleans into the `consents` array shape
    // the backend serializer expects (task #23). Only include the kinds
    // the doctor actually ticked. `data_sharing_with_employer` is
    // optional — never required, just captured when ticked. The two
    // required kinds are gated by `consentsOk` above, so if we got
    // here we know both are ticked.
    payload.consents = []
    if (payload.consent_tos) payload.consents.push('terms_of_service')
    if (payload.consent_telehealth) payload.consents.push('telehealth_treatment')
    if (payload.consent_data_sharing) payload.consents.push('data_sharing_with_employer')
    delete payload.consent_tos
    delete payload.consent_telehealth
    delete payload.consent_data_sharing
    try {
      const patient = await mut.mutateAsync(payload)
      setForm(EMPTY_FORM)
      setOpen(false)
      onRegistered?.(patient, { startEncounter })
    } catch (err) {
      // Task #24 — dedup guard hit. Stash candidates + the exact payload we
      // sent so the modal's "Register anyway" button can retry with
      // force_create=true. Other errors fall through to the existing
      // ErrorBanner (surfaced via mut.isError below).
      if (isLikelyDuplicatePatientError(err)) {
        setDupCandidates(getDuplicatePatientCandidates(err))
        setPendingSubmit({ payload, startEncounter })
        // Reset the mutation so mut.error/isError don't linger — otherwise
        // ErrorBanner would flash "candidates: [object Object]" (firstError()
        // stringifies the candidates array) the moment the user dismisses the
        // modal via "Back to form". Modal now owns the 409 UX entirely.
        mut.reset()
      }
      /* other errors surfaced via mut.isError below */
    }
  }

  // Task #24 — modal action handlers.
  async function handleUseExisting(candidate) {
    // The doctor confirmed this candidate IS the walk-in. Close the modal
    // + navigate to that patient's chart; encounter creation happens there.
    setDupCandidates(null)
    setPendingSubmit(null)
    setForm(EMPTY_FORM)
    setOpen(false)
    if (candidate?.id) navigate(`/doctor/patients/${candidate.id}`)
  }

  async function handleForceCreate() {
    // The doctor confirmed the collision is a false positive (e.g. shared
    // phone in a family). Retry the same payload with force_create=true.
    if (!pendingSubmit) return
    const { payload, startEncounter } = pendingSubmit
    try {
      const patient = await mut.mutateAsync({ ...payload, force_create: true })
      setDupCandidates(null)
      setPendingSubmit(null)
      setForm(EMPTY_FORM)
      setOpen(false)
      onRegistered?.(patient, { startEncounter })
    } catch {
      /* surfaced via mut.isError below; modal stays open so the doctor
         sees the error banner + can retry or cancel */
    }
  }

  function handleCancelDupModal() {
    setDupCandidates(null)
    setPendingSubmit(null)
    // Leave the form as-is so the doctor can adjust fields (e.g. correct
    // a typo in the phone number that caused the false-positive match).
  }

  if (!open) {
    return (
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-800">Register a new patient</div>
            <div className="text-xs text-slate-500">Not on the list? Onboard a walk-in and start their first encounter.</div>
          </div>
          <button
            onClick={() => setOpen(true)}
            className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2"
          >
            Register patient
          </button>
        </div>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader
        title="Register a new patient"
        subtitle="Required: first/last name AND at least one of email or phone (so the patient has a recovery channel). Everything else can be updated from the chart later."
        right={
          <button onClick={() => setOpen(false)} className="text-xs underline text-slate-500">Cancel</button>
        }
      />

      <div className="grid gap-3 md:grid-cols-2">
        <LabeledInput label="First name *" value={form.first_name} onChange={set('first_name')} autoFocus />
        <LabeledInput label="Last name *" value={form.last_name} onChange={set('last_name')} />
        <LabeledSelect label="Sex" value={form.sex} onChange={set('sex')}>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </LabeledSelect>
        <LabeledInput label="Date of birth" type="date" value={form.date_of_birth} onChange={set('date_of_birth')} />
        <LabeledInput
          label={`Phone number${hasPhone || hasEmail ? '' : ' (or email →)'}`}
          value={form.phone_number} onChange={set('phone_number')}
          placeholder="+675 7000 1234"
        />
        <LabeledInput
          label={`Email${hasEmail || hasPhone ? '' : ' (or phone ←)'}`}
          type="email"
          value={form.email_in} onChange={set('email_in')}
          placeholder="patient@example.com"
        />
        <LabeledInput label="Place of residence" value={form.place_of_residence} onChange={set('place_of_residence')} placeholder="Port Moresby, NCD" />
        <LabeledSelect label="Category" value={form.patient_category} onChange={setCategory}>
          <option value="general">General</option>
          <option value="government">Government agency</option>
          <option value="company">Company</option>
          <option value="institution">Institution</option>
          <option value="other_group">Other group</option>
        </LabeledSelect>
        <LabeledInput label="Emergency contact — name" value={form.emergency_contact_name} onChange={set('emergency_contact_name')} />
        <LabeledInput label="Emergency contact — phone" value={form.emergency_contact_phone} onChange={set('emergency_contact_phone')} />
      </div>

      {/* Org binder via employer dropdown. Linking the patient to their
          subscribing org here is what makes ALL their care (consults,
          labs, imaging, meds) roll up into that org's "value delivered"
          with the configured markup. No link → the patient's activity is
          credited to no org. Leave the employer unselected to register a
          standalone walk-in (equivalent to General category). */}
      {showOrgPanel && (
        <div className="mt-3 p-3 bg-wantok-50/60 border border-wantok-100 rounded-lg space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-wantok-800">
            Link to subscribing organisation
          </div>
          <p className="text-[11px] text-slate-600">
            Select the patient's employer / sponsoring organisation. This
            attributes their care to that org's subscription so it shows in
            the org dashboard's value delivered. Leave blank to register as
            a standalone walk-in (switch the category back to General).
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <LabeledSelect
              label="Organisation"
              value={form.organization_id}
              onChange={set('organization_id')}
            >
              <option value="">
                {orgsQuery.isLoading ? 'Loading organisations…' : 'Select organisation…'}
              </option>
              {(orgsQuery.data || []).map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </LabeledSelect>
            {orgsQuery.isError && (
              <div className="text-[11px] text-amber-700 self-end">
                Couldn't load organisations — try reopening the form.
              </div>
            )}
            {form.organization_id && (
              <>
                <LabeledInput
                  label="Employee / payroll ID (optional)"
                  value={form.employee_id_in}
                  onChange={set('employee_id_in')}
                  placeholder="e.g. 1234"
                />
                <LabeledInput
                  label="Department (optional)"
                  value={form.department_in}
                  onChange={set('department_in')}
                  placeholder="e.g. Field operations"
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Consent capture (task #23) — HIPAA-material. The doctor must
          confirm with the patient before submit is enabled. Wording
          parallels the public /register wizard so the doctor can read it
          aloud verbatim. Data-sharing is optional (only relevant when the
          patient is being linked to an employer). */}
      <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-700">
          Patient consent (confirm with the patient before submitting)
        </div>
        <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={form.consent_tos}
            onChange={(e) => setForm((f) => ({ ...f, consent_tos: e.target.checked }))}
          />
          <span>
            <span className="font-medium">Terms of Service</span> — the patient accepts
            Wantok Telehealth's Terms of Service and Privacy Notice.
            <span className="text-rose-600 ml-1">*</span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={form.consent_telehealth}
            onChange={(e) => setForm((f) => ({ ...f, consent_telehealth: e.target.checked }))}
          />
          <span>
            <span className="font-medium">Telehealth Treatment Consent</span> — the
            patient consents to being assessed and treated via telehealth, where
            clinically appropriate.
            <span className="text-rose-600 ml-1">*</span>
          </span>
        </label>
        {showOrgPanel && (
          <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.consent_data_sharing}
              onChange={(e) => setForm((f) => ({ ...f, consent_data_sharing: e.target.checked }))}
            />
            <span>
              <span className="font-medium">Data-sharing with employer</span> — the
              patient consents to aggregated (de-identified) utilisation summaries
              being visible to their subscribing organisation. Optional.
            </span>
          </label>
        )}
        {!consentsOk && (
          <div className="text-[11px] text-rose-700 mt-1">
            Both starred (<span className="text-rose-600">*</span>) consents are required to register.
          </div>
        )}
      </div>

      {!contactOk && (form.first_name || form.last_name) && (
        <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          Add an email OR a phone number so the patient can recover their account if they forget their password.
        </div>
      )}

      {/* Suppress ErrorBanner when the dup modal is handling the 409 — it
          isn't really a failure state from the doctor's perspective, they
          just need to make a choice. Other errors still surface here. */}
      {mut.isError && !dupCandidates && (
        <div className="mt-3"><ErrorBanner error={mut.error} /></div>
      )}

      <div className="mt-4 flex flex-wrap gap-2 justify-end">
        <button
          onClick={() => submit(false)}
          disabled={mut.isPending || !formValid}
          className="text-sm bg-white border border-wantok-700 text-wantok-700 rounded px-3 py-2 disabled:opacity-60"
        >
          {mut.isPending ? 'Saving…' : 'Register only'}
        </button>
        <button
          onClick={() => submit(true)}
          disabled={mut.isPending || !formValid}
          className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
        >
          {mut.isPending ? 'Saving…' : 'Register & start encounter'}
        </button>
      </div>

      {/* Task #24 — dedup guard modal. Rendered outside the form flow via
          fixed-position overlay; only visible when the backend flagged
          candidates. Wired to the useNavigate + mutation retry above. */}
      {dupCandidates && dupCandidates.length > 0 && (
        <DuplicatePatientModal
          candidates={dupCandidates}
          onUseExisting={handleUseExisting}
          onForceCreate={handleForceCreate}
          onCancel={handleCancelDupModal}
          submitting={mut.isPending}
          contextMessage={
            'A patient with matching details is already registered. ' +
            "Confirm with the walk-in that they aren't already in the system " +
            'before creating a new record.'
          }
          actionLabels={{
            useExisting: 'Open this patient',
            forceCreate: 'Register new anyway',
            cancel: 'Back to form',
          }}
        />
      )}
    </Card>
  )
}

function LabeledInput({ label, ...rest }) {
  return (
    <label className="text-xs text-slate-600 flex flex-col gap-1">
      <span>{label}</span>
      <input
        {...rest}
        className="border rounded px-2 py-1.5 text-sm"
      />
    </label>
  )
}

function LabeledSelect({ label, children, ...rest }) {
  return (
    <label className="text-xs text-slate-600 flex flex-col gap-1">
      <span>{label}</span>
      <select {...rest} className="border rounded px-2 py-1.5 text-sm bg-white">
        {children}
      </select>
    </label>
  )
}

/**
 * ListSearch — reusable search input for any long list. Drop above a
 * <ul>; pair with a `matches*()` filter on the rendered items. Includes
 * a magnifying-glass icon, an inline X clear-button when there's text,
 * and the same navy-blue input typography as the rest of the app.
 *
 * Used by:
 *   - Doctor dashboard: My Draft Encounters, Submitted Encounters
 * Pattern is intentionally identical so the affordance reads the same
 * everywhere it appears.
 */
function ListSearch({ value, onChange, placeholder = 'Search…' }) {
  return (
    <div className="relative mb-3">
      <span
        aria-hidden
        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        style={{ fontSize: '14px' }}
      >
        ⌕
      </span>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-slate-300 rounded-md pl-8 pr-9 py-2 text-sm focus:border-wantok-600 focus:outline-none focus:ring-1 focus:ring-wantok-600"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 inline-flex items-center justify-center text-base leading-none"
        >
          ×
        </button>
      )}
    </div>
  )
}

/**
 * matchesEncounter — case-insensitive substring search across the
 * encounter's most useful identification fields. Returns true when:
 *   - q is empty / whitespace
 *   - any of the candidate strings contain q
 *
 * Candidates: patient name (full + first/last), MRN, DOB, chief
 * complaint, encounter id. Tolerates missing fields.
 */
function matchesEncounter(enc, q) {
  if (!q) return true
  const needle = String(q).trim().toLowerCase()
  if (!needle) return true
  const candidates = [
    enc.patient_name,
    enc.patient?.full_name,
    enc.patient?.first_name,
    enc.patient?.last_name,
    enc.patient_mrn,
    enc.patient?.mrn,
    enc.patient_dob,
    enc.patient?.date_of_birth,
    enc.chief_complaint,
    String(enc.id ?? ''),
  ]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase())
  return candidates.some((c) => c.includes(needle))
}
